// SPEC-43 · the admin's skeletons: the shape of the base and of an
// athlete's profile while the server answers, in the cards' own tints, so
// nothing moves when the data arrives. The whole skeleton breathes, opacity
// only (.skeleton-breathe), and holds still under reduced motion. A screen
// reader hears the screen's wait sentence, as before; the shapes are hidden
// from it.
//
// AdminFallback, in the app's first chunk, draws them before the admin's
// own chunk and its gate are there; the base and the profile draw them
// under their real header while their call runs.
//
// SPEC-47: they take the loaded screens' shapes at each width, with the same
// breakpoints: the phone's header on two rows, its counters two by two, its
// cards in place of the table, her name over her status on a profile.

import type { ReactNode } from "react";

import { PyfLogo } from "../components/PyfLogo";
import { useCopy } from "../i18n/LanguageProvider";
import { profileInAddress } from "./route";
import {
  assessmentsColumn,
  generalColumn,
  pageFrame,
  pageTitle,
  profileColumns,
  profileHead,
  profileHeadPerson,
  rowMiddle,
  rowTitle,
  segmentTrack,
  statGrid,
} from "./styles";

/** One placeholder: a rounded bar, sized by the caller. */
function Bar({ className }: { className: string }) {
  return <span className={`block shrink-0 rounded-pill bg-line-8 ${className}`} />;
}

/** A field's or a button's pill at the controls' height, white on the cream page; the caller gives its width. */
const control = "h-[var(--control-h-sm)] rounded-pill bg-white";

/** A line of text's height, with its bar in the middle: the card keeps the height the words will give it. */
function Line({ height, bar }: { height: string; bar: string }) {
  return (
    <span className={`flex items-center ${height}`}>
      <Bar className={bar} />
    </span>
  );
}

/** The wait, announced once; the shapes are for the eyes only, stacked as the loaded screen stacks its sections. */
function Waiting({ message, children }: { message: string; children: ReactNode }) {
  return (
    <div role="status" className="skeleton-breathe flex flex-col gap-6">
      <span className="sr-only">{message}</span>
      <div aria-hidden="true" className="contents">
        {children}
      </div>
    </div>
  );
}

const TONES = {
  neutral: "bg-cream-deep",
  lime: "bg-lime-tint",
  violet: "bg-violet-tint",
  orange: "bg-orange-tint",
} as const;

type Tone = keyof typeof TONES;

/** A counter card, StatCard's padding and three lines: title, number, foot. */
function StatCardSkeleton({ tone }: { tone: Tone }) {
  return (
    <div className={`flex flex-col gap-3 rounded-card p-5 ${TONES[tone]}`}>
      <Line height="h-[21px]" bar="h-3 w-28" />
      <Line height="h-8" bar="h-6 w-20" />
      <Line height="h-[18px]" bar="h-2.5 w-36" />
    </div>
  );
}

function StatGridSkeleton({ tones }: { tones: readonly Tone[] }) {
  return (
    <div className={statGrid}>
      {tones.map((tone) => (
        <StatCardSkeleton key={tone} tone={tone} />
      ))}
    </div>
  );
}

/** Rows the table's skeleton draws: a screen's worth, not the base's count, which is not known yet. */
const TABLE_ROWS = 6;

/**
 * The table's columns, where UserTable's usually start on a desktop; a
 * column may shrink below its bars on a tablet, and its bars with it.
 */
const tableColumns =
  "grid grid-cols-[minmax(0,319fr)_minmax(0,225fr)_minmax(0,107fr)_minmax(0,183fr)_minmax(0,279fr)_minmax(0,127fr)] items-center gap-x-2 px-4 desk:gap-x-0 desk:px-5";

/** The column labels' widths, one per column. */
const HEADER_BARS = ["w-20", "w-24", "w-12", "w-20", "w-28", "w-12"] as const;

function RowSkeleton() {
  return (
    <div className={`${tableColumns} h-[65.5px] border-t border-line-8`}>
      <span className="flex items-center gap-3">
        <span className="size-9 shrink-0 rounded-pill bg-violet-tint" />
        <span className="flex min-w-0 flex-col gap-2">
          <Bar className="h-3 w-20 max-w-full" />
          <Bar className="h-2.5 w-36 max-w-full" />
        </span>
      </span>
      <span className="flex min-w-0 flex-col gap-2">
        <Bar className="h-3 w-24 max-w-full" />
        <Bar className="h-2.5 w-28 max-w-full" />
      </span>
      <Bar className="h-3 w-16 max-w-full" />
      <span className="flex min-w-0 items-center gap-2 desk:gap-3">
        <Bar className="h-3 w-8" />
        <Bar className="h-1 w-12 desk:w-20" />
      </span>
      <span className="flex flex-col gap-2">
        <Bar className="h-2.5 w-12 max-w-full" />
        <Bar className="h-2.5 w-12 max-w-full" />
      </span>
      <Bar className="h-7 w-20 max-w-full" />
    </div>
  );
}

/** A phone's card (UserCards): her avatar, name and club, her tag; under them the count, the bar and the day. */
function CardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-card-inner border border-line-8 p-3.5">
      <span className="flex items-center gap-3">
        <span className="size-9 shrink-0 rounded-pill bg-violet-tint" />
        <span className="flex min-w-0 flex-1 flex-col">
          <Line height="h-[21px]" bar="h-3 w-24" />
          <Line height="h-[19.5px]" bar="h-2.5 w-36 max-w-full" />
        </span>
        <Bar className="h-7 w-20" />
      </span>
      <span className="flex h-[21.5px] items-center gap-3">
        <Bar className="h-3 w-8" />
        <Bar className="h-1 min-w-0 flex-1" />
        <Bar className="h-2.5 w-28" />
      </span>
    </div>
  );
}

/** The four counters and the table: what the base draws under its title. */
export function BaseSkeleton() {
  const text = useCopy().admin;
  return (
    <Waiting message={text.loading}>
      <StatGridSkeleton tones={["neutral", "lime", "violet", "orange"]} />
      <div className="flex flex-col overflow-hidden rounded-card bg-white pt-2">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-3 pb-4 tablet:px-5">
          <Line height="h-[17px]" bar="h-2.5 w-24" />
          <span className={`${segmentTrack} h-11 w-full tablet:w-[340px]`} />
        </div>
        <div className="max-tablet:hidden">
          <div className={`${tableColumns} h-[37px] pt-2 pb-3`}>
            {HEADER_BARS.map((width, index) => (
              <Bar key={index} className={`h-2.5 max-w-full ${width}`} />
            ))}
          </div>
          {Array.from({ length: TABLE_ROWS }, (_, index) => (
            <RowSkeleton key={index} />
          ))}
        </div>
        <div className="flex flex-col gap-2 px-4 pb-4 tablet:hidden">
          {Array.from({ length: TABLE_ROWS }, (_, index) => (
            <CardSkeleton key={index} />
          ))}
        </div>
      </div>
    </Waiting>
  );
}

/** The search field, the export and « + Nova utilizadora » (SPEC-54), at their size and place in the base's bar. */
export function BaseToolsSkeleton() {
  return (
    <div aria-hidden="true" className="skeleton-breathe flex w-full flex-wrap items-center gap-3 desk:w-auto">
      <span className={`${control} w-full tablet:w-80`} />
      <span className={`${control} w-36`} />
      <span className={`${control} w-48 max-desk:ml-auto`} />
    </div>
  );
}

/** « Hoje · seg 19 out » under the base's title, before the server's day is known. */
export function DayLineSkeleton() {
  return (
    <span aria-hidden="true" className="skeleton-breathe">
      <Line height="h-[19.5px]" bar="h-2.5 w-28" />
    </span>
  );
}

/** Rows of Dados gerais, and assessments: the profile's own counts. */
const GENERAL_ROWS = 8;
const ASSESSMENT_ROWS = 9;

/** Her header, the four counters, Dados gerais and her assessments: what a profile draws under « ← Base ». */
export function ProfileSkeleton() {
  const text = useCopy().admin.athlete;
  return (
    <Waiting message={text.loading}>
      <div className={profileHead}>
        <div className={profileHeadPerson}>
          <span className="size-18 shrink-0 rounded-pill bg-violet-tint" />
          <span className="flex min-w-0 flex-col gap-3">
            <Bar className="h-8 w-48 max-w-full" />
            <Bar className="h-3 w-72 max-w-full" />
          </span>
        </div>
        <div className="col-start-2 flex items-center gap-3">
          <Bar className="h-7 w-20" />
          <span className={`${control} w-44`} />
        </div>
      </div>
      <StatGridSkeleton tones={["lime", "violet", "orange", "neutral"]} />
      <div className={profileColumns}>
        <div className={generalColumn}>
          <Line height="h-[16.5px]" bar="h-2.5 w-28" />
          <div className="rounded-card bg-white px-5 py-2">
            {Array.from({ length: GENERAL_ROWS }, (_, index) => (
              <div key={index} className="flex h-[46px] items-center justify-between gap-4 border-t border-line-8 first:h-[45px] first:border-t-0">
                <Bar className="h-3 w-20" />
                <Bar className="h-3 w-28" />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1 rounded-card-inner bg-violet-tint p-4">
            <Line height="h-[21px]" bar="h-3 w-40" />
            <span className="flex flex-col">
              <Line height="h-[21px]" bar="h-3 w-full" />
              <Line height="h-[21px]" bar="h-3 w-3/4" />
            </span>
          </div>
        </div>
        <div className={assessmentsColumn}>
          <Line height="h-[16.5px]" bar="h-2.5 w-36" />
          <div className="flex flex-col gap-3">
            {Array.from({ length: ASSESSMENT_ROWS }, (_, index) => (
              <div key={index} className="flex items-center gap-3 rounded-card-inner border border-line-10 bg-white px-4 py-4">
                <span className="size-10 shrink-0 rounded-glyph bg-cream-deep" />
                <span className={rowMiddle}>
                  <span className={`${rowTitle} h-[42px] justify-center gap-2`}>
                    <Bar className="h-3 w-44 max-w-full" />
                    <Bar className="h-2.5 w-32 max-w-full" />
                  </span>
                  <Bar className="h-7 w-20" />
                </span>
                {/* The arrow's column, kept on every loaded row. */}
                <span className="w-4 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Waiting>
  );
}

/** The header's shape: the logo, its label, the two sections, the language and « App → ». */
function HeaderSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pt-6 tablet:gap-x-6">
      {/* The logo holds still; the rest breathes. On a phone, AdminHeader's two rows. */}
      <div className="flex min-w-0 items-center gap-3 max-tablet:grow max-tablet:basis-40 tablet:gap-4">
        <PyfLogo className="w-10 shrink-0 text-dark" />
        <span className="skeleton-breathe min-w-0 grow">
          <Bar className="h-2.5 w-64 max-w-full" />
        </span>
      </div>
      <span className={`skeleton-breathe ${segmentTrack} h-11 w-52 max-tablet:order-1`} />
      <div className="skeleton-breathe ml-auto flex items-center gap-5 max-tablet:contents xl:ml-0">
        <span className={`${segmentTrack} h-11 w-36`} />
        <Bar className="h-3 w-12 max-tablet:order-2 max-tablet:ml-auto" />
      </div>
    </div>
  );
}

/**
 * The whole admin page before its chunk and its gate are there: the base,
 * or the profile the address opens. A visitor with no team session sees it
 * for that moment, then the sign-in card or the refusal.
 */
export function AdminPageSkeleton() {
  const text = useCopy().admin;
  const profile = profileInAddress();
  return (
    <div className={pageFrame}>
      <div className="mx-auto flex w-full max-w-[1360px] flex-col">
        <HeaderSkeleton />
        <main className="flex flex-col gap-6 pt-10 pb-12">
          {profile ? (
            <>
              {/* « ← Base de utilizadoras », the way back. */}
              <span aria-hidden="true" className="skeleton-breathe">
                <Line height="h-[21px]" bar="h-3 w-44" />
              </span>
              <ProfileSkeleton />
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <h1 className={pageTitle}>{text.title}</h1>
                  <DayLineSkeleton />
                </div>
                <BaseToolsSkeleton />
              </div>
              <BaseSkeleton />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
